import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { Button, Flex, Tooltip, message, Popconfirm, Checkbox } from 'antd';
import { useDrag, useDrop } from 'react-dnd';
import Icon from '../../components/common/Icon';
import { FileName, FloorListRowInner, FloorListRowInnerDraggIcon, FloorListRowOuter } from './Floor.style';
import theme from '../../config/theme';
import { useSelector } from 'react-redux';
import Input from '../../components/common/Input';
import { _updateFloor, _deleteFloor, _changeDefaultBuildingFloor, _swapFloors } from '../../shared/httpservice/apis';
import { debounce } from 'lodash';
export const FileRow = ({ data, onSwap, fetchBuildingsAndFloors, defaultFloorId, alignClicked, isLastFloor, floors }) => {
    const siteId = useSelector((state) => state?.site?.checkSite?.response?.data?.siteId);
    const [floorName, setFloorName] = useState(null);
    const [isChecked, setIsChecked] = useState(data.floorId === defaultFloorId);
    useEffect(() => {
        setIsChecked(data?.floorId === defaultFloorId);
    }, [defaultFloorId, data?.floorId]);
    useEffect(() => {
        if (data?.name) {
            setFloorName(data?.shortName);
        }
    }, [data]);
    const [{ isDragging }, dragRef] = useDrag({
        type: 'file-row',
        item: { id: data.floorId },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });
    const [, dropRef] = useDrop({
        accept: 'file-row',
        hover: (item) => {
            if (item.id !== data.floorId) {
                onSwap(item.id, data.floorId); // Swap the floors and elevations
            }
        },
    });
    const ref = (node) => node && dragRef(dropRef(node));
    const handleBlur = () => {
        if (siteId && floorName) {
            return _updateFloor(siteId, data?.floorId, floorName).then((res) => {
                if (res?.status === 200) {
                    fetchBuildingsAndFloors();
                }
            });
        }
    };
    // Create a debounced version of the API call
    const debouncedUpdateFloor = useMemo(() => debounce((siteId, floorId, floorName) => {
        if (siteId) {
            _updateFloor(siteId, floorId, floorName).then((res) => {
                if (res?.status === 200) {
                    fetchBuildingsAndFloors();
                }
            });
        }
    }, 500), // Adjust debounce delay (500ms) as needed
    [siteId, fetchBuildingsAndFloors, _updateFloor]);
    const handleChange = (e) => {
        const value = e.target.value;
        setFloorName(value);
        // Call debounced function
        debouncedUpdateFloor(siteId, data?.floorId, value);
    };
    const confirm = async () => {
        try {
            if (siteId && floors && data) {
                const targetElevation = data.elevation;
                if (targetElevation < floors.length - 1) {
                    for (let i = targetElevation; i < floors.length - 1; i++) {
                        const currentFloorId = floors[i]?.floorId;
                        const nextFloorId = floors[i + 1]?.floorId;
                        if (currentFloorId && nextFloorId) {
                            await _swapFloors(currentFloorId, nextFloorId);
                            const temp = floors[i];
                            floors[i] = floors[i + 1];
                            floors[i + 1] = temp;
                        }
                    }
                }
                await _deleteFloor(siteId, data?.floorId).then((res) => {
                    if (res?.status === 200) {
                        fetchBuildingsAndFloors();
                    }
                });
            }
        }
        catch (error) {
            console.error(error);
        }
    };
    const cancel = (e) => {
    };
    const handleRadioChange = async () => {
        if (siteId && data?.buildingId && defaultFloorId !== data?.floorId) {
            try {
                await _changeDefaultBuildingFloor(data?.buildingId, siteId, data?.floorId);
                fetchBuildingsAndFloors();
                message.success('Default floor updated successfully!');
            }
            catch (error) {
                message.error('Failed to update default floor.');
                console.error(error);
            }
        }
    };
    return (_jsxs(FloorListRowOuter, { className: isDragging ? 'dragging' : '', ref: ref, border: isLastFloor, children: [_jsx(FloorListRowInnerDraggIcon, { children: _jsx(Icon, { icon: "drag", size: 20 }) }), _jsxs(FloorListRowInner, { children: [_jsxs(Flex, { align: "center", justify: "start", gap: 8, children: [_jsx(Icon, { icon: "file-attachment-02", size: 18, style: { flex: '0 0 18px' }, color: theme.textEmphasis.muted }), _jsx(FileName, { children: data?.name })] }), _jsx(Input, { variant: "filled", name: "elevation", type: "text", disabled: true, value: `${data?.elevation}` }), _jsx(Input, { maxLength: 20, name: "label", variant: "filled", type: "text", placeholder: "Eg: Ground Floor", value: typeof floorName === 'string' ? floorName : '', onChange: (e) => {
                            setFloorName(e.target.value);
                        },
                        // onChange={handleChange}
                        onBlur: handleBlur, error: floorName === '' ? 'Floor name is required' : false }), _jsx(Checkbox, { value: data?.floorId, checked: isChecked, onChange: handleRadioChange })] }), _jsx("div", { className: "pe-3", children: _jsx(Tooltip, { title: "Delete Floor", children: _jsx(Popconfirm, { title: "Delete the floor", description: "Are you sure to delete this floor?", onConfirm: confirm, onCancel: cancel, okText: "Yes", cancelText: "No", children: _jsx(Button, { type: "text", icon: _jsx(Icon, { icon: "trash-03", size: 20 }) }) }) }) })] }));
};
